import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import PageHeader from "@/components/base/PageHeader";
import EmptyState from "@/components/base/EmptyState";
import Pagination from "@/components/base/Pagination";
import { brandArticles, brandCategories, brandSortOptions } from "@/mocks/brand";

const PAGE_SIZE = 4;

export default function BrandList() {
  const [category, setCategory] = useState("全部");
  const [sort, setSort] = useState(brandSortOptions[0]);
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    let list = brandArticles.filter(
      (item) => category === "全部" || item.category === category,
    );
    if (sort === "最早发布") {
      list = [...list].sort((a, b) => a.date.localeCompare(b.date));
    } else if (sort === "标题排序") {
      list = [...list].sort((a, b) => a.title.localeCompare(b.title, "zh-Hans-CN"));
    } else {
      list = [...list].sort((a, b) => b.date.localeCompare(a.date));
    }
    return list;
  }, [category, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const visible = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const resetFilters = () => {
    setCategory("全部");
    setSort(brandSortOptions[0]);
    setPage(1);
  };

  return (
    <div className="w-full">
      <PageHeader
        breadcrumb={[{ label: "品牌介绍" }]}
        eyebrow="品牌档案"
        title="品牌介绍"
        description="从品牌历史、产品线到发展历程与企业文化，系统了解想要XX 的成长轨迹与核心价值。"
      />

      <section className="px-4 md:px-8 py-10 md:py-14">
        <div className="mx-auto max-w-6xl flex flex-col gap-8">
          <div className="flex flex-col gap-4 rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
            <div className="flex flex-wrap items-center gap-2">
              <span className="mr-1 text-sm font-medium text-foreground-700">主题筛选</span>
              {brandCategories.map((item) => (
                <button
                  key={item}
                  type="button"
                  onClick={() => {
                    setCategory(item);
                    setPage(1);
                  }}
                  className={`rounded-full px-4 py-1.5 text-sm transition-colors whitespace-nowrap ${
                    category === item
                      ? "bg-primary-500 text-background-50"
                      : "bg-background-100 text-foreground-600 hover:bg-primary-50 hover:text-primary-600"
                  }`}
                >
                  {item}
                </button>
              ))}
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-background-200 pt-4">
              <span className="text-sm text-foreground-500">
                共 <strong className="font-semibold text-foreground-900">{filtered.length}</strong> 篇专题
              </span>
              <label className="flex items-center gap-2 text-sm text-foreground-600">
                排序
                <select
                  value={sort}
                  onChange={(e) => {
                    setSort(e.target.value);
                    setPage(1);
                  }}
                  className="rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-800 focus:border-primary-400 focus:outline-none"
                >
                  {brandSortOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          </div>

          {visible.length === 0 ? (
            <EmptyState
              title="没有找到相关品牌介绍"
              description="当前筛选条件下暂无内容，试着更换主题或排序方式后再查看。"
              actionLabel="重置筛选"
              onAction={resetFilters}
            />
          ) : (
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              {visible.map((item) => (
                <Link
                  key={item.id}
                  to={`/brand/${item.id}`}
                  className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300"
                >
                  <div className="h-52 w-full overflow-hidden">
                    <img
                      src={item.cover}
                      alt={item.title}
                      className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="flex flex-1 flex-col gap-3 p-6">
                    <div className="flex flex-wrap items-center gap-3 text-xs text-foreground-500">
                      <span className="rounded-full bg-secondary-100 px-2.5 py-1 font-medium text-secondary-900">
                        {item.category}
                      </span>
                      <span>{item.date}</span>
                      <span className="flex items-center gap-1">
                        <i className="ri-time-line" />
                        {item.readingTime}
                      </span>
                    </div>
                    <h2 className="font-heading text-lg font-semibold leading-snug text-foreground-950 group-hover:text-primary-600">
                      {item.title}
                    </h2>
                    <p className="text-sm leading-relaxed text-foreground-600 line-clamp-3">
                      {item.summary}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          )}

          {visible.length > 0 && (
            <Pagination page={currentPage} totalPages={totalPages} onChange={setPage} />
          )}
        </div>
      </section>
    </div>
  );
}